<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignsAPI from 'dashboard/api/campaigns';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import CampaignIntelligenceReportsAPI from 'dashboard/api/campaignIntelligenceReports';
import WhatsappContactPreferencesAPI from 'dashboard/api/whatsappContactPreferences';
import { messageStamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    required: true,
  },
  initialTab: {
    type: String,
    default: 'health',
  },
});

const { t } = useI18n();

const activeTab = ref(props.initialTab);
const intelligence = ref(null);
const health = ref(null);
const report = ref(null);
const consentPreferences = ref([]);
const consentStatus = ref('opted_out');
const consentQuery = ref('');
const consentSource = ref('');
const consentConfidence = ref('');
const consentScope = ref('');
const consentPage = ref(1);
const consentMeta = ref({});
const selectedPreference = ref(null);
const backfillPreview = ref(null);
const consentReason = ref('');
const isLoading = ref(false);
const isLoadingConsent = ref(false);
const isLoadingPreference = ref(false);
const isGeneratingReport = ref(false);
const isRunningBackfillPreview = ref(false);
const isUpdatingConsent = ref(false);
const error = ref('');

const percent = value => `${Number(value || 0).toFixed(2)}%`;

const tabs = computed(() => [
  { key: 'health', label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.TABS.HEALTH') },
  {
    key: 'analytics',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.TABS.ANALYTICS'),
  },
  {
    key: 'templates',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.TABS.TEMPLATES'),
  },
  { key: 'consent', label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.TABS.CONSENT') },
  { key: 'reports', label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.TABS.REPORTS') },
]);

const funnel = computed(() => intelligence.value?.funnel || {});
const scope = computed(() => intelligence.value?.scope || {});
const campaigns = computed(() => intelligence.value?.campaign_rankings || []);
const templates = computed(() => intelligence.value?.template_rankings || []);
const recommendations = computed(
  () => intelligence.value?.recommendations || []
);
const consent = computed(() => intelligence.value?.consent || {});
const latestHealth = computed(
  () => health.value?.latest || intelligence.value?.health?.latest || {}
);
const consentStatuses = computed(() => [
  {
    value: 'opted_out',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.OPTED_OUT'),
  },
  {
    value: 'opted_in',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.OPTED_IN'),
  },
  {
    value: 'candidate_opt_out',
    label: t(
      'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.CANDIDATE',
      'Needs review'
    ),
  },
]);

const consentSourceOptions = computed(() => [
  { value: '', label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.ALL_SOURCES') },
  {
    value: 'manual',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SOURCE_MANUAL'),
  },
  {
    value: 'keyword',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SOURCE_KEYWORD'),
  },
  {
    value: 'historical_keyword',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SOURCE_HISTORICAL'),
  },
  {
    value: 'candidate_keyword',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SOURCE_CANDIDATE'),
  },
  {
    value: 'whatsapp_native',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SOURCE_NATIVE'),
  },
]);

const consentScopeOptions = computed(() => [
  { value: '', label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.ALL_SCOPES') },
  {
    value: 'marketing_campaigns',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SCOPE_MARKETING'),
  },
  {
    value: 'all_whatsapp_outbound',
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SCOPE_ALL_OUTBOUND'),
  },
]);

const topCampaigns = computed(() => {
  return [...campaigns.value]
    .sort((a, b) => b.reply_rate - a.reply_rate)
    .slice(0, 5);
});

const worstCampaigns = computed(() => {
  return [...campaigns.value]
    .sort((a, b) => b.failure_rate - a.failure_rate)
    .slice(0, 5);
});

const dateRange = computed(() => {
  const until = new Date();
  const since = new Date();
  since.setDate(until.getDate() - 30);
  return {
    since: since.toISOString().slice(0, 10),
    until: until.toISOString().slice(0, 10),
  };
});

const metricCards = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.CAMPAIGNS'),
    value: scope.value.campaign_count || 0,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.RECIPIENTS'),
    value: scope.value.recipient_count || 0,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.DELIVERY_RATE'),
    value: percent(funnel.value.delivery_rate),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.READ_RATE'),
    value: percent(funnel.value.read_rate),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.REPLY_RATE'),
    value: percent(funnel.value.reply_rate),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.FAILURE_RATE'),
    value: percent(funnel.value.failure_rate),
  },
]);

const fetchData = async ({ refreshHealth = false } = {}) => {
  if (!props.inboxId) return;

  isLoading.value = true;
  error.value = '';
  try {
    const [intelligenceResponse, healthResponse] = await Promise.all([
      CampaignsAPI.intelligence({ inboxId: props.inboxId, ...dateRange.value }),
      InboxHealthAPI.getCampaignHealth(props.inboxId, {
        ...dateRange.value,
        refresh: refreshHealth,
      }),
    ]);
    intelligence.value = intelligenceResponse.data;
    health.value = healthResponse.data;
  } catch {
    error.value = t('CAMPAIGN.WHATSAPP.INTELLIGENCE.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const generateReport = async () => {
  isGeneratingReport.value = true;
  error.value = '';
  try {
    const response = await CampaignIntelligenceReportsAPI.createReport({
      inbox_id: props.inboxId,
      ...dateRange.value,
      title: t('CAMPAIGN.WHATSAPP.INTELLIGENCE.REPORTS.DEFAULT_TITLE'),
    });
    report.value = response.data.report;
  } catch {
    error.value = t('CAMPAIGN.WHATSAPP.INTELLIGENCE.REPORTS.ERROR');
  } finally {
    isGeneratingReport.value = false;
  }
};

const fetchConsentPreferences = async () => {
  if (!props.inboxId) return;

  isLoadingConsent.value = true;
  try {
    const response = await WhatsappContactPreferencesAPI.list({
      inbox_id: props.inboxId,
      status: consentStatus.value,
      q: consentQuery.value || undefined,
      source: consentSource.value || undefined,
      confidence: consentConfidence.value || undefined,
      subscription_scope: consentScope.value || undefined,
      page: consentPage.value,
    });
    consentPreferences.value = response.data.preferences || [];
    consentMeta.value = response.data.meta || {};
  } catch {
    error.value = t('CAMPAIGN.WHATSAPP.INTELLIGENCE.ERROR');
  } finally {
    isLoadingConsent.value = false;
  }
};

const showPreferenceDetails = async preference => {
  selectedPreference.value = preference;
  isLoadingPreference.value = true;
  try {
    const response = await WhatsappContactPreferencesAPI.show(
      preference.contact_id,
      {
        inbox_id: props.inboxId,
        include_events: true,
      }
    );
    selectedPreference.value = response.data.preference;
  } catch {
    error.value = t('CAMPAIGN.WHATSAPP.INTELLIGENCE.ERROR');
  } finally {
    isLoadingPreference.value = false;
  }
};

const previewBackfill = async () => {
  if (!props.inboxId) return;

  isRunningBackfillPreview.value = true;
  try {
    const response = await WhatsappContactPreferencesAPI.backfillPreview({
      inbox_id: props.inboxId,
      confidence_threshold: 100,
      include_candidates: true,
    });
    backfillPreview.value = response.data;
  } catch {
    error.value = t('CAMPAIGN.WHATSAPP.INTELLIGENCE.ERROR');
  } finally {
    isRunningBackfillPreview.value = false;
  }
};

const updateConsent = async (preference, status) => {
  const reason = consentReason.value.trim();
  if (!reason) {
    error.value = t(
      'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.REASON_REQUIRED',
      'Enter a reason before changing consent.'
    );
    return;
  }

  isUpdatingConsent.value = true;
  error.value = '';
  try {
    await WhatsappContactPreferencesAPI.updatePreference(
      preference.contact_id,
      {
        inbox_id: props.inboxId,
        status,
        reason,
        subscription_scope:
          preference.subscription_scope ||
          consentScope.value ||
          'marketing_campaigns',
      }
    );
    consentReason.value = '';
    await fetchConsentPreferences();
    await fetchData();
  } catch {
    error.value = t('CAMPAIGN.WHATSAPP.INTELLIGENCE.ERROR');
  } finally {
    isUpdatingConsent.value = false;
  }
};

const reviewCandidate = async (preference, reviewAction) => {
  const reason = consentReason.value.trim();
  const eventId = preference.latest_event?.id;
  if (!reason || !eventId) {
    error.value = t(
      'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.REASON_REQUIRED',
      'Enter a reason before changing consent.'
    );
    return;
  }

  isUpdatingConsent.value = true;
  error.value = '';
  try {
    await WhatsappContactPreferencesAPI.reviewCandidate({
      inbox_id: props.inboxId,
      event_id: eventId,
      review_action: reviewAction,
      reason,
    });
    consentReason.value = '';
    selectedPreference.value = null;
    await fetchConsentPreferences();
    await fetchData();
  } catch {
    error.value = t('CAMPAIGN.WHATSAPP.INTELLIGENCE.ERROR');
  } finally {
    isUpdatingConsent.value = false;
  }
};

const formatDate = value => {
  if (!value) return '-';
  return messageStamp(new Date(value).getTime() / 1000, 'dd/MM/yyyy, HH:mm');
};

const statusLabel = status =>
  t(`CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.STATUS_LABELS.${status}`, status);

const scopeLabel = scopeName =>
  t(
    `CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SCOPE_LABELS.${scopeName}`,
    scopeName || '-'
  );

const sourceLabel = source =>
  t(
    `CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SOURCE_LABELS.${source}`,
    source || '-'
  );

const healthTone = value => {
  const quality = String(value || '').toLowerCase();
  if (['green', 'high'].includes(quality)) return 'text-n-teal-11';
  if (['yellow', 'medium'].includes(quality)) return 'text-n-amber-11';
  if (['red', 'low'].includes(quality)) return 'text-n-ruby-11';
  return 'text-n-slate-11';
};

watch(
  () => props.inboxId,
  () => {
    fetchData();
    fetchConsentPreferences();
  }
);

watch(
  () => activeTab.value,
  tab => {
    if (tab === 'consent') fetchConsentPreferences();
  }
);

watch(
  () => consentStatus.value,
  () => {
    consentPage.value = 1;
    fetchConsentPreferences();
  }
);

watch(
  [consentQuery, consentSource, consentConfidence, consentScope, consentPage],
  () => fetchConsentPreferences()
);

onMounted(() => {
  fetchData();
  if (activeTab.value === 'consent') fetchConsentPreferences();
});
</script>

<template>
  <section class="mb-8 border border-n-slate-4 rounded-lg bg-n-surface-2">
    <div
      class="flex items-center justify-between gap-3 p-4 border-b border-n-slate-4"
    >
      <div class="min-w-0">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          icon="i-lucide-refresh-cw"
          size="sm"
          variant="faded"
          color="slate"
          @click="fetchData({ refreshHealth: true })"
        />
        <Button
          :label="t('CAMPAIGN.WHATSAPP.INTELLIGENCE.REPORTS.GENERATE')"
          icon="i-lucide-file-text"
          size="sm"
          :is-loading="isGeneratingReport"
          @click="generateReport"
        />
      </div>
    </div>

    <div class="flex gap-1 px-4 pt-4 overflow-x-auto">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap"
        :class="
          activeTab === tab.key
            ? 'border-n-teal-9 text-n-slate-12'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center justify-center p-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else class="p-4">
      <div v-if="error" class="mb-4 text-sm font-medium text-n-ruby-11">
        {{ error }}
      </div>

      <div v-if="activeTab === 'health'" class="space-y-4">
        <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div class="p-3 border border-n-slate-4 rounded-md">
            <div class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.HEALTH.QUALITY') }}
            </div>
            <div
              class="mt-1 text-lg font-semibold"
              :class="healthTone(latestHealth.quality_rating)"
            >
              {{ latestHealth.quality_rating || '-' }}
            </div>
          </div>
          <div class="p-3 border border-n-slate-4 rounded-md">
            <div class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.HEALTH.TIER') }}
            </div>
            <div class="mt-1 text-lg font-semibold text-n-slate-12">
              {{
                latestHealth.effective_messaging_limit ||
                latestHealth.messaging_limit_tier ||
                '-'
              }}
            </div>
          </div>
          <div class="p-3 border border-n-slate-4 rounded-md">
            <div class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.HEALTH.PHONE') }}
            </div>
            <div class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ latestHealth.display_phone_number || '-' }}
            </div>
          </div>
          <div class="p-3 border border-n-slate-4 rounded-md">
            <div class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.HEALTH.SNAPSHOTS') }}
            </div>
            <div class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ health?.snapshots?.length || 0 }}
            </div>
          </div>
        </div>

        <div class="space-y-2">
          <div
            v-for="alert in health?.alerts || []"
            :key="alert"
            class="px-3 py-2 text-sm border rounded-md text-n-amber-12 bg-n-amber-2 border-n-amber-5"
          >
            {{ alert }}
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'analytics'" class="space-y-4">
        <div class="grid grid-cols-2 gap-3 md:grid-cols-3">
          <div
            v-for="metric in metricCards"
            :key="metric.label"
            class="p-3 border border-n-slate-4 rounded-md"
          >
            <div class="text-xs font-medium text-n-slate-10">
              {{ metric.label }}
            </div>
            <div class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ metric.value }}
            </div>
          </div>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div>
            <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.ANALYTICS.BEST') }}
            </h3>
            <div class="space-y-2">
              <div
                v-for="campaign in topCampaigns"
                :key="campaign.id"
                class="flex items-center justify-between gap-3 p-2 border border-n-slate-4 rounded-md"
              >
                <span class="text-sm font-medium truncate text-n-slate-12">{{
                  campaign.title
                }}</span>
                <span class="text-sm text-n-slate-11">{{
                  percent(campaign.reply_rate)
                }}</span>
              </div>
            </div>
          </div>
          <div>
            <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.ANALYTICS.RISK') }}
            </h3>
            <div class="space-y-2">
              <div
                v-for="campaign in worstCampaigns"
                :key="campaign.id"
                class="flex items-center justify-between gap-3 p-2 border border-n-slate-4 rounded-md"
              >
                <span class="text-sm font-medium truncate text-n-slate-12">{{
                  campaign.title
                }}</span>
                <span class="text-sm text-n-ruby-11">{{
                  percent(campaign.failure_rate)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'templates'" class="space-y-2">
        <div
          v-for="template in templates"
          :key="template.template_name"
          class="grid grid-cols-2 gap-3 p-3 border border-n-slate-4 rounded-md md:grid-cols-5"
        >
          <div class="col-span-2 min-w-0">
            <div class="text-sm font-semibold truncate text-n-slate-12">
              {{ template.template_name }}
            </div>
            <div class="text-xs text-n-slate-10">
              {{ template.campaign_count }}
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.TEMPLATES.CAMPAIGNS') }}
            </div>
          </div>
          <div class="text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.READ_RATE') }}:
            {{ percent(template.read_rate) }}
          </div>
          <div class="text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.REPLY_RATE') }}:
            {{ percent(template.reply_rate) }}
          </div>
          <div class="text-sm text-n-ruby-11">
            {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.METRICS.FAILURE_RATE') }}:
            {{ percent(template.failure_rate) }}
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'consent'" class="space-y-4">
        <div class="grid grid-cols-3 gap-3">
          <div class="p-3 border border-n-slate-4 rounded-md">
            <div class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.OPTED_IN') }}
            </div>
            <div class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ consent.opted_in || 0 }}
            </div>
          </div>
          <div class="p-3 border border-n-slate-4 rounded-md">
            <div class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.OPTED_OUT') }}
            </div>
            <div class="mt-1 text-xl font-semibold text-n-ruby-11">
              {{ consent.opted_out || 0 }}
            </div>
          </div>
          <div class="p-3 border border-n-slate-4 rounded-md">
            <div class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.LEGACY') }}
            </div>
            <div class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ consent.legacy_opt_out || 0 }}
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex gap-1">
              <button
                v-for="status in consentStatuses"
                :key="status.value"
                class="px-3 py-1.5 text-xs font-medium rounded-md border"
                :class="
                  consentStatus === status.value
                    ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-12'
                    : 'border-n-slate-4 text-n-slate-11 hover:text-n-slate-12'
                "
                @click="consentStatus = status.value"
              >
                {{ status.label }}
              </button>
            </div>
            <Button
              :label="
                t(
                  'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.BACKFILL_PREVIEW',
                  'Preview backfill'
                )
              "
              icon="i-lucide-search"
              size="sm"
              variant="faded"
              color="slate"
              :is-loading="isRunningBackfillPreview"
              @click="previewBackfill"
            />
          </div>

          <div class="grid gap-2 md:grid-cols-5">
            <input
              v-model="consentQuery"
              class="px-3 py-2 text-sm border rounded-md border-n-slate-4 bg-n-surface-1 text-n-slate-12 placeholder:text-n-slate-9 md:col-span-2"
              type="search"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SEARCH_PLACEHOLDER')
              "
            />
            <select
              v-model="consentSource"
              class="px-3 py-2 text-sm border rounded-md border-n-slate-4 bg-n-surface-1 text-n-slate-12"
            >
              <option
                v-for="source in consentSourceOptions"
                :key="source.value"
                :value="source.value"
              >
                {{ source.label }}
              </option>
            </select>
            <select
              v-model="consentScope"
              class="px-3 py-2 text-sm border rounded-md border-n-slate-4 bg-n-surface-1 text-n-slate-12"
            >
              <option
                v-for="scopeOption in consentScopeOptions"
                :key="scopeOption.value"
                :value="scopeOption.value"
              >
                {{ scopeOption.label }}
              </option>
            </select>
            <input
              v-model="consentConfidence"
              class="px-3 py-2 text-sm border rounded-md border-n-slate-4 bg-n-surface-1 text-n-slate-12 placeholder:text-n-slate-9"
              type="number"
              min="0"
              max="100"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.CONFIDENCE')
              "
            />
          </div>

          <input
            v-model="consentReason"
            class="w-full px-3 py-2 text-sm border rounded-md border-n-slate-4 bg-n-surface-1 text-n-slate-12 placeholder:text-n-slate-9"
            type="text"
            :placeholder="
              t(
                'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.REASON_PLACEHOLDER',
                'Reason for manual changes'
              )
            "
          />
        </div>

        <div
          v-if="backfillPreview"
          class="grid grid-cols-2 gap-2 p-3 border rounded-md border-n-amber-5 bg-n-amber-2 md:grid-cols-4"
        >
          <div class="text-xs text-n-amber-12">
            <div class="font-medium">
              {{
                t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SIGNALS', 'Signals')
              }}
            </div>
            <div class="mt-1 text-sm font-semibold">
              {{ backfillPreview.counts?.contacts_with_signal || 0 }}
            </div>
          </div>
          <div class="text-xs text-n-amber-12">
            <div class="font-medium">
              {{
                t(
                  'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.MISSING',
                  'Missing suppression'
                )
              }}
            </div>
            <div class="mt-1 text-sm font-semibold">
              {{ backfillPreview.counts?.missing_persisted_suppression || 0 }}
            </div>
          </div>
          <div class="text-xs text-n-amber-12">
            <div class="font-medium">
              {{
                t(
                  'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.CANDIDATES',
                  'Candidate messages'
                )
              }}
            </div>
            <div class="mt-1 text-sm font-semibold">
              {{ backfillPreview.counts?.candidate_opt_out_messages || 0 }}
            </div>
          </div>
          <div class="text-xs text-n-amber-12">
            <div class="font-medium">
              {{
                t(
                  'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.AFTER_SIGNAL',
                  'Rows after signal'
                )
              }}
            </div>
            <div class="mt-1 text-sm font-semibold">
              {{
                backfillPreview.counts?.campaign_rows_after_first_signal || 0
              }}
            </div>
          </div>
        </div>

        <div class="overflow-x-auto border border-n-slate-4 rounded-md">
          <table class="min-w-full text-sm">
            <thead class="text-left bg-n-alpha-1 text-n-slate-10">
              <tr>
                <th class="px-3 py-2 font-medium">
                  {{
                    t(
                      'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.CONTACT',
                      'Contact'
                    )
                  }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{
                    t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.STATUS', 'Status')
                  }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{
                    t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SCOPE', 'Scope')
                  }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{
                    t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.SOURCE', 'Source')
                  }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{
                    t(
                      'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.UPDATED',
                      'Updated'
                    )
                  }}
                </th>
                <th class="px-3 py-2 font-medium text-right">
                  {{
                    t(
                      'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.ACTIONS',
                      'Actions'
                    )
                  }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isLoadingConsent">
                <td colspan="6" class="px-3 py-4 text-center text-n-slate-11">
                  <Spinner />
                </td>
              </tr>
              <template v-else>
                <tr
                  v-for="preference in consentPreferences"
                  :key="preference.contact_id"
                  class="border-t border-n-slate-4"
                >
                  <td class="px-3 py-2">
                    <button
                      type="button"
                      class="text-left"
                      @click="showPreferenceDetails(preference)"
                    >
                      <div class="font-medium text-n-slate-12">
                        {{ preference.contact_name || '-' }}
                      </div>
                      <div class="text-xs text-n-slate-10">
                        {{ preference.phone_number || '-' }}
                      </div>
                    </button>
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ statusLabel(preference.status) }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ scopeLabel(preference.subscription_scope) }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{
                      sourceLabel(
                        preference.latest_event?.source || preference.source
                      )
                    }}
                    <div
                      v-if="preference.latest_event?.matched_rule"
                      class="text-xs text-n-slate-9"
                    >
                      {{ preference.latest_event.matched_rule }}
                    </div>
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{
                      formatDate(
                        preference.latest_event?.occurred_at ||
                          preference.updated_at
                      )
                    }}
                  </td>
                  <td class="px-3 py-2">
                    <div class="flex justify-end gap-2">
                      <Button
                        v-if="
                          preference.latest_event?.review_status === 'pending'
                        "
                        :label="
                          t(
                            'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.ACCEPT',
                            'Accept'
                          )
                        "
                        size="xs"
                        variant="faded"
                        color="ruby"
                        :disabled="isUpdatingConsent"
                        @click="reviewCandidate(preference, 'accept')"
                      />
                      <Button
                        v-if="
                          preference.latest_event?.review_status === 'pending'
                        "
                        :label="
                          t(
                            'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.DISMISS',
                            'Dismiss'
                          )
                        "
                        size="xs"
                        variant="faded"
                        color="slate"
                        :disabled="isUpdatingConsent"
                        @click="reviewCandidate(preference, 'dismiss')"
                      />
                      <Button
                        :label="
                          t(
                            'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.MARK_OUT',
                            'Opt out'
                          )
                        "
                        size="xs"
                        variant="faded"
                        color="ruby"
                        :disabled="isUpdatingConsent"
                        :is-loading="isUpdatingConsent"
                        @click="updateConsent(preference, 'opted_out')"
                      />
                      <Button
                        :label="
                          t(
                            'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.MARK_IN',
                            'Opt in'
                          )
                        "
                        size="xs"
                        variant="faded"
                        color="slate"
                        :disabled="isUpdatingConsent"
                        @click="updateConsent(preference, 'opted_in')"
                      />
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="!isLoadingConsent && consentPreferences.length === 0">
                <td colspan="6" class="px-3 py-4 text-center text-n-slate-11">
                  {{
                    t(
                      'CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.EMPTY',
                      'No consent records found.'
                    )
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          class="flex items-center justify-between gap-3 text-xs text-n-slate-10"
        >
          <span>
            {{
              t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.TOTAL', {
                count: consentMeta.total_count || 0,
              })
            }}
          </span>
          <div class="flex items-center gap-2">
            <Button
              :label="t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.PREV')"
              size="xs"
              variant="ghost"
              color="slate"
              :disabled="consentPage <= 1"
              @click="consentPage -= 1"
            />
            <span>
              {{
                t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.PAGE', {
                  page: consentMeta.current_page || consentPage,
                })
              }}
            </span>
            <Button
              :label="t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.NEXT')"
              size="xs"
              variant="ghost"
              color="slate"
              :disabled="
                (consentMeta.current_page || consentPage) * 25 >=
                (consentMeta.total_count || 0)
              "
              @click="consentPage += 1"
            />
          </div>
        </div>

        <div
          v-if="selectedPreference"
          class="p-3 border rounded-md border-n-slate-4 bg-n-surface-1"
        >
          <div class="flex items-center justify-between gap-2 mb-3">
            <div>
              <div class="text-sm font-semibold text-n-slate-12">
                {{ selectedPreference.contact_name || '-' }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ selectedPreference.phone_number || '-' }}
              </div>
            </div>
            <Button
              icon="i-lucide-x"
              size="xs"
              variant="ghost"
              color-scheme="secondary"
              @click="selectedPreference = null"
            />
          </div>
          <div
            v-if="isLoadingPreference"
            class="py-4 text-center text-n-slate-11"
          >
            <Spinner />
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="event in selectedPreference.events || []"
              :key="event.id"
              class="p-2 border rounded-md border-n-slate-4"
            >
              <div class="flex flex-wrap items-center gap-2 text-xs">
                <span class="font-semibold text-n-slate-12">
                  {{ statusLabel(event.status) }}
                </span>
                <span class="text-n-slate-10">
                  {{ sourceLabel(event.source) }}
                </span>
                <span class="text-n-slate-10">
                  {{ formatDate(event.occurred_at) }}
                </span>
                <span
                  v-if="event.review_status"
                  class="px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11"
                >
                  {{ event.review_status }}
                </span>
              </div>
              <div class="mt-1 text-xs text-n-slate-11">
                {{ event.reason || '-' }}
              </div>
              <div
                v-if="event.matched_rule || event.matched_text"
                class="mt-1 text-xs text-n-slate-10"
              >
                {{ event.matched_rule || '-' }}
                <span v-if="event.matched_text">
                  · {{ event.matched_text }}
                </span>
              </div>
            </div>
            <div
              v-if="!(selectedPreference.events || []).length"
              class="text-sm text-n-slate-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.INTELLIGENCE.CONSENT.NO_EVENTS') }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'reports'" class="space-y-3">
        <div
          v-for="recommendation in recommendations"
          :key="recommendation.text"
          class="p-3 text-sm border rounded-md border-n-slate-4 text-n-slate-12"
        >
          <span class="font-semibold">{{ recommendation.type }}</span>
          <span class="text-n-slate-10"> · </span>
          {{ recommendation.text }}
        </div>
        <div
          v-if="report"
          class="p-3 text-sm border rounded-md border-n-teal-5 bg-n-teal-2 text-n-teal-12"
        >
          {{
            t('CAMPAIGN.WHATSAPP.INTELLIGENCE.REPORTS.CREATED', {
              id: report.id,
            })
          }}
        </div>
      </div>
    </div>
  </section>
</template>
